import * as yup from 'yup';

import {ADD_ON_TYPE, FORMAT} from 'Utils/consts';
import {createCustomSchema, getCustomFieldDefaultValues} from 'Utils/customValueUtils';
import getUserCountryDialCode from 'Utils/phoneCountryCodes';

const {hasValidCharactersEmail, normalizeQueryParamName} = require('Utils');

export const MAX_EMAIL_LEN = 254;
export const MAX_BOOKER_NAME_LEN = 128;

const getFormValueByKey = (fv, key) => {
  const item = fv?.find((item) => item.label.toLowerCase() === key.toLowerCase());
  return item;
};

export const getBookingFormDefaultValues = (appointment, fvString, searchParams = {}) => {
  // support pre-populate fields when opening a new booking page
  const formContextStr = window.sessionStorage.getItem('formContext');
  if (formContextStr) {
    return JSON.parse(formContextStr);
  }
  const fvParsed = JSON.parse(fvString || '[]');

  const customFields = appointment?.customFields || [];

  const bookerFirstName = searchParams['firstname'] || getFormValueByKey(fvParsed, 'First Name')?.value || '';
  const bookerLastName = searchParams['lastname'] || getFormValueByKey(fvParsed, 'Last Name')?.value || '';
  const bookerEmail = searchParams['email'] || getFormValueByKey(fvParsed, 'Email Address')?.value || '';
  const bookerPhoneCountryCode = getFormValueByKey(fvParsed, 'Phone Number')?.value?.countryCode ||
    getUserCountryDialCode() || '';
  const bookerPhoneNumber = getFormValueByKey(fvParsed, 'Phone Number')?.value?.phoneNumber || '';

  // parse prefilled location info
  const {location, locationkind} = searchParams;
  const prefilledLocationInfo = {
    kind: locationkind,
  };
  if (prefilledLocationInfo.kind === ADD_ON_TYPE.OUT_BOUND_CALL) {
    const arr = location.split(' ');
    prefilledLocationInfo.countryCode = arr[0];
    prefilledLocationInfo.phoneNumber = arr[1];
  } else {
    prefilledLocationInfo.location = location;
  }
  // parse prefilled custom question answers
  const prefilledCustomValues = customFields.map((cf) => {
    const prefilledValue = searchParams[normalizeQueryParamName(cf.name).toLowerCase()];
    let values;
    if (prefilledValue) {
      if (cf.format === FORMAT.CHOICES_MANY) {
        values = prefilledValue.split(',');
      } else {
        values = [prefilledValue];
      }
    }
    return {
      position: cf.position,
      values,
    };
  }).filter((item) => !!(item.values));

  const existingCustomValues = prefilledCustomValues || (fvParsed?.map((item) => {
    const customField = customFields.find((cf) => cf.name.toLowerCase() === item.label.toLowerCase());
    if (!customField) return item;

    let values = [item.value];
    if (customField.format === FORMAT.CHOICES_MANY) {
      values = item.value.filter((v) => v.checked).map((v) => v.value);
    } else if (customField.format === FORMAT.PHONE_NUMBER) {
      values = [`${item.value.countryCode} ${item.value.phoneNumber}`];
    }

    return {
      ...item,
      position: customField?.position || -1,
      values,
    };
  }));

  return {
    bookerFirstName,
    bookerLastName,
    bookerEmail,
    bookerPhoneCountryCode,
    bookerPhoneNumber,
    bookerSendSmsUpdates: true,
    customFields: getCustomFieldDefaultValues(customFields, existingCustomValues),
    guests: searchParams['guests']? searchParams['guests'].split(','): [],
    locationConfiguration: getBookAndRescheduleDefaultLocation(appointment, prefilledLocationInfo),
  };
};

export const getBookAndRescheduleDefaultLocation = (appointment, prefilledLocationInfo) => {
  const locations = appointment?.locationConfigurations || [];
  const hasOnlyOneLocation = locations.length === 1;
  if (locations.length) {
    return {
      kind: (prefilledLocationInfo?.kind) || (hasOnlyOneLocation? locations[0].kind: ''),
      location: (prefilledLocationInfo?.location) || (hasOnlyOneLocation? locations[0].location: ''),
      additionalInfo: '',
      outboundPhoneCountryCode: (prefilledLocationInfo?.countryCode) || getUserCountryDialCode() || '',
      outboundPhoneNumber: (prefilledLocationInfo?.phoneNumber) || '',
    };
  }
};

export const formatBookAndRescheduleLocation = (formValues) => {
  if (formValues.locationConfiguration) {
    const {kind, location, outboundPhoneCountryCode = '', outboundPhoneNumber = ''} =
      formValues.locationConfiguration;
    return {
      locationConfiguration: {
        kind,
        location: location || '',
        phoneNumber: kind === ADD_ON_TYPE.OUT_BOUND_CALL?
        (outboundPhoneCountryCode.split('-')[0] + ' ' + outboundPhoneNumber): '',
      },
    };
  }
  return null;
};

export const bookAndRescheduleLocationSchema = (t) => {
  return yup.lazy((value) => {
    if (value) {
      return yup.object({
        kind: yup.string().required(t('validation.mustSelectLocation')),
        // if use `askInvitee`, invitee must fill the location.
        location: yup.string().when('kind', (kind) => {
          if (kind === ADD_ON_TYPE.ASK_INVITEE) {
            return yup.string().required(t('validation.fieldIsRequired', {fieldName: t('apptForm.location')}));
          }
        }),
        // if use `outboundCall` (host call invitee), invitee must fill the country code and phone number.
        outboundPhoneCountryCode: yup.string().when('kind', (kind) => {
          if (kind === ADD_ON_TYPE.OUT_BOUND_CALL) {
            return yup.string().matches(/^\+[0-9-]{1,5}$/, 'Invalid country code');
          }
        }),
        outboundPhoneNumber: yup.string().when('kind', (kind) => {
          if (kind === ADD_ON_TYPE.OUT_BOUND_CALL) {
            return yup.string().matches(/^[0-9]{5,20}$/, t('validation.invalidPhoneNumber'));
          }
        }),
      });
    }
    return yup.mixed().notRequired();
  });
};

export const getBookerValidationSchema = (appointment, isCciAccount, voterInfo, t) => {
  const whitelist = appointment?.whitelist;
  const customFields = appointment?.customFields || [];
  // const locations = appointment?.locationConfigurations || [];
  let emailValidations = yup
    .string('Type email here')
    .test('emailAddressFormat', t('booking.warningInvalidEmail'),
      (inputString) => {
        if (voterInfo !== null) {
          return true;
        }
        return hasValidCharactersEmail(inputString);
      }
    )
    .max(MAX_EMAIL_LEN, `Can not be longer than ${MAX_EMAIL_LEN} characters`)
    .lowercase();

  if (whitelist) {
    emailValidations = yup
      .string('Type email here')
      .test('emailAddressFormat', t('booking.warningInvalidEmail'),
        (inputString) => {
          return hasValidCharactersEmail(inputString);
        }
      )
      .lowercase()
      .oneOf(whitelist, 'This email is not whitelisted by the event host')
      .required('Email is required');
  }

  return yup.object({
    bookerFirstName: yup
      .string('First Name')
      .max(MAX_BOOKER_NAME_LEN, `Can not be longer than ${MAX_BOOKER_NAME_LEN} characters`)
      .concat(voterInfo === null ?
        yup.string().required('First Name is required') :
        yup.string()),
    bookerLastName: yup
      .string('Last Name')
      .max(MAX_BOOKER_NAME_LEN, `Can not be longer than ${MAX_BOOKER_NAME_LEN} characters`)
      .concat(voterInfo === null ?
        yup.string().required('Last Name is required') :
        yup.string()),
    bookerEmail: emailValidations,
    bookerSendSmsUpdates: yup.boolean(),
    bookerPhoneCountryCode: yup
      .string()
      .when(['bookerSendSmsUpdates', 'bookerPhoneNumber'], (bookerSendSmsUpdates, bookerPhoneNumber) => {
        if ((bookerSendSmsUpdates || isCciAccount) && bookerPhoneNumber) {
          return yup.string('Country Code')
            .required('Country code is required')
            .matches(/^\+[0-9-]{1,5}$/, 'Invalid country code');
        }
      }),
    bookerPhoneNumber: yup
      .string()
      .when('bookerSendSmsUpdates', (bookerSendSmsUpdates) => {
        if (bookerSendSmsUpdates || isCciAccount) {
          return yup.string('Phone Number').test(
            'empty-or-valid-dight-check',
            t('validation.invalidPhoneNumber'),
            (tel) => !tel || /^[0-9]{5,20}$/.test(tel),
          );
        }
      }),
    customFields: yup.object(createCustomSchema(customFields)),
    guests: yup.array().of(yup
      .string('Type email here')
      .lowercase()
      .email('Please provide a valid email address')),
    locationConfiguration: bookAndRescheduleLocationSchema(t),
  });
};
